import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Megaphone, Loader2, ExternalLink, ArrowRight } from "lucide-react";
import { PageShell } from "@/components/Navbar";
import { Fade } from "@/components/proptech";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";

const CATEGORY_LABELS = {
  PRODUCT_UPDATE: "Product update", ANNOUNCEMENT: "Announcement",
  OPERATIONAL: "Important update", MARKETING: "Update",
};

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch { return ""; }
};

export default function CommunicationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [comm, setComm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/communications/${id}`)
      .then((r) => { if (alive) setComm(r.data); })
      .catch((e) => { if (alive) setError(apiError(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [id]);

  const openCta = () => {
    const url = (comm?.cta_url || "").trim();
    if (!url) return;
    if (url.startsWith("/") && !url.startsWith("//")) { nav(url); return; }
    try {
      const u = new URL(url);
      if (u.protocol === "https:" && u.host === window.location.host) { nav(u.pathname + u.search + u.hash); return; }
      if (u.protocol === "https:") { window.open(url, "_blank", "noopener,noreferrer"); return; }
    } catch { /* unsafe/invalid → ignore */ }
  };

  const ctaIsExternal = (() => {
    const url = (comm?.cta_url || "").trim();
    if (!url || url.startsWith("/")) return false;
    try { return new URL(url).host !== window.location.host; } catch { return false; }
  })();

  return (
    <PageShell testid="communication-detail-page">
      <div className="max-w-2xl mx-auto">
        <button
          data-testid="comm-back-btn"
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors mb-6"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        {loading && (
          <div className="flex items-center gap-2 text-slate-500 py-16 justify-center" data-testid="comm-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading communication…
          </div>
        )}

        {!loading && error && (
          <div className="bg-slate-50 border border-dashed border-border rounded-2xl p-12 text-center" data-testid="comm-error">
            <Megaphone className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="font-display text-lg font-semibold text-slate-800">This communication isn’t available</p>
            <p className="text-slate-500 mt-1">{error}</p>
            <Button data-testid="comm-error-notifs" onClick={() => nav("/notifications")} variant="outline" className="rounded-xl mt-5">
              Back to notifications
            </Button>
          </div>
        )}

        {!loading && comm && (
          <Fade>
            <article className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="comm-article">
              {/* Branded header */}
              <div className="bg-[#0B1D36] px-6 py-5 sm:px-8">
                <div className="flex items-center gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#D4A017]/15 text-[#D4A017] uppercase tracking-wide"
                    data-testid="comm-category">
                    <Megaphone className="h-3.5 w-3.5" />
                    {CATEGORY_LABELS[comm.category] || "Announcement"}
                  </span>
                </div>
                <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white leading-snug" data-testid="comm-title">
                  {comm.subject}
                </h1>
                <p className="text-slate-300 text-sm mt-2" data-testid="comm-date">
                  From PropertyConnect · {fmtDate(comm.sent_at)}
                </p>
              </div>

              {/* Body */}
              <div className="px-6 py-7 sm:px-8">
                <div
                  className="comm-body max-w-none text-slate-800 text-[15px] sm:text-base"
                  data-testid="comm-body"
                  dangerouslySetInnerHTML={{ __html: comm.body_html || "" }}
                />

                {comm.cta_label && comm.cta_url && (
                  <div className="mt-8">
                    <Button
                      data-testid="comm-cta-btn"
                      onClick={openCta}
                      className="rounded-xl bg-[#0B1D36] hover:bg-[#0B1D36]/90 text-white h-11 px-6"
                    >
                      {comm.cta_label}
                      {ctaIsExternal ? <ExternalLink className="h-4 w-4 ml-2" /> : <ArrowRight className="h-4 w-4 ml-2" />}
                    </Button>
                  </div>
                )}
              </div>

              {/* Footer brand strip */}
              <div className="bg-slate-50 border-t border-border px-6 py-4 sm:px-8">
                <p className="text-[11px] font-semibold tracking-[0.18em] text-[#D4A017]">CONNECT • MATCH • COLLABORATE • CLOSE</p>
                <p className="text-xs text-slate-400 mt-1">propconnectai.com</p>
              </div>
            </article>
          </Fade>
        )}
      </div>
    </PageShell>
  );
}
